import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Alert,
  CircularProgress,
  Typography,
  LinearProgress
} from '@mui/material';
import GatewaySelect from '../selects/GatewaySelect';
import { Invoice } from '../../types';
import invoiceService from '../../services/invoiceService';

interface ProcessBoletoModalProps {
  open: boolean;
  onClose: () => void;
  /** Uma fatura (menu da linha) */
  invoice?: Invoice | null;
  /** Várias faturas (processamento em massa); tem precedência se length > 0 */
  invoices?: Invoice[];
  onSuccess?: () => void;
}

const ProcessBoletoModal: React.FC<ProcessBoletoModalProps> = ({
  open,
  onClose,
  invoice,
  invoices,
  onSuccess
}) => {
  const targets: Invoice[] =
    invoices && invoices.length > 0 ? invoices : invoice ? [invoice] : [];

  const [formData, setFormData] = useState({
    gateway_id: undefined as number | undefined
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });

  const isBulk = targets.length > 1;

  useEffect(() => {
    if (open) {
      setFormData({ gateway_id: undefined });
      setError(null);
      setProgress({ current: 0, total: 0 });
    }
  }, [open, targets.length]);

  const handleGatewayChange = (gatewayId: number | null) => {
    setFormData(prev => ({
      ...prev,
      gateway_id: gatewayId || undefined
    }));
  };

  const handleProcess = async () => {
    if (!formData.gateway_id) {
      setError('Selecione um gateway de pagamento');
      return;
    }
    if (targets.length === 0) {
      setError('Nenhuma fatura para processar');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setProgress({ current: 0, total: targets.length });

      const erros: string[] = [];

      for (let i = 0; i < targets.length; i++) {
        const inv = targets[i];
        setProgress({ current: i + 1, total: targets.length });
        try {
          const response = await invoiceService.processBoleto(inv.id, formData.gateway_id);
          if (!response.success) {
            erros.push(`#${inv.id}: ${response.message || 'Erro ao processar'}`);
          }
        } catch (err) {
          erros.push(
            `#${inv.id}: ${err instanceof Error ? err.message : 'Erro ao processar boleto'}`
          );
        }
      }

      if (erros.length === 0) {
        if (onSuccess) {
          onSuccess();
        }
        onClose();
      } else if (erros.length < targets.length) {
        setError(
          `Concluído com avisos (${erros.length} de ${targets.length} falharam):\n${erros.join('\n')}`
        );
        if (onSuccess) {
          onSuccess();
        }
      } else {
        setError(erros.join('\n'));
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao processar boleto');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isBulk ? `Processar boletos em massa (${targets.length})` : 'Processar Boleto'}
      </DialogTitle>

      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {isBulk && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Serão processadas {targets.length} fatura(s) no gateway selecionado.
            </Typography>
          )}

          {error && (
            <Alert severity={error.includes('avisos') ? 'warning' : 'error'} sx={{ mb: 2, whiteSpace: 'pre-wrap' }}>
              {error}
            </Alert>
          )}

          {loading && isBulk && progress.total > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Processando {progress.current} / {progress.total}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={(progress.current / progress.total) * 100}
                sx={{ mt: 0.5 }}
              />
            </Box>
          )}

          <GatewaySelect
            value={formData.gateway_id || undefined}
            onChange={handleGatewayChange}
            label="Gateway de Pagamento"
            required={false}
            helperText="Gateway opcional para geração automática de boletos/PIX"
            showOnlyActive={true}
            size="medium"
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleProcess}
          variant="contained"
          disabled={loading || !formData.gateway_id || targets.length === 0}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          Processar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ProcessBoletoModal;
